// src/components/notification/NotificationSettings.jsx
import React, { useState, useEffect } from 'react';
import notificationService from '../../services/notificationService';
import './notificationSettings.scss';

const NotificationSettings = ({ userId: propUserId, centerId: propCenterId, inModal = false }) => {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Manage userId and centerId from props or localStorage
  const userId = propUserId || localStorage.getItem('user_id');
  const centerId = propCenterId || localStorage.getItem('center_id');

  useEffect(() => {
    if (userId && centerId) {
      initializeNotifications();
    } else {
      setError('User ID or Center ID not available');
      setLoading(false);
    }
  }, [userId, centerId]);

  const initializeNotifications = async () => {
    try {
      setLoading(true);

      // Check if notifications are supported
      const supported = notificationService.isSupported();
      setIsSupported(supported);

      if (!supported) {
        setError('Push notifications are not supported in this browser');
        return;
      }

      // Get current permission status
      setPermission(Notification.permission);

      // Initialize service worker
      await notificationService.initializeServiceWorker();

      // Get current subscription status
      const status = await notificationService.getStatus(userId);
      setIsSubscribed(status.total_subscriptions > 0);  // Updated based on backend response

      // Get notification settings
      const centerSettings = await notificationService.getSettings(userId);
      setSettings(centerSettings);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    try {
      setLoading(true);

      // Request permission if not granted
      if (permission !== 'granted') {
        const newPermission = await notificationService.requestPermission();
        setPermission(newPermission);
      }

      // Subscribe to notifications
      await notificationService.subscribe(userId);
      setIsSubscribed(true);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribe = async () => {
    try {
      setLoading(true);
      await notificationService.unsubscribe(userId);
      setIsSubscribed(false);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTestNotification = async () => {
    try {
      await notificationService.sendTestNotification(userId);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateSettings = async (newSettings) => {
    try {
      setLoading(true);
      const updated = await notificationService.updateSettings(userId, centerId, newSettings);
      setSettings(updated);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="notification-loading">Loading notification settings...</div>;
  }

  if (!isSupported) {
    return (
      <div className="notification-warning">
        Push notifications are not supported in your browser.
        Please use a modern browser like Chrome, Firefox, or Edge.
      </div>
    );
  }

  return (
    <div className={`notification-settings ${inModal ? 'in-modal' : ''}`}>
      <h3>Push Notifications</h3>

      {error && (
        <div className="error-message">{error}</div>
      )}

      <div className="notification-status">
        <p>Status: <span className={isSubscribed ? 'subscribed' : 'not-subscribed'}>
          {isSubscribed ? 'Subscribed' : 'Not Subscribed'}
        </span></p>
        <p>Permission: <span className={`permission-${permission}`}>{permission}</span></p>
      </div>

      <div className="notification-actions">
        {!isSubscribed ? (
          <button
            onClick={handleSubscribe}
            disabled={loading}
            className="btn btn-primary"
          >
            Enable Notifications
          </button>
        ) : (
          <>
            <button
              onClick={handleUnsubscribe}
              disabled={loading}
              className="btn btn-secondary"
            >
              Disable Notifications
            </button>
            <button
              onClick={handleTestNotification}
              disabled={loading}
              className="btn btn-info"
            >
              Send Test Notification
            </button>
          </>
        )}
      </div>

      {isSubscribed && settings && (
        <div className="notification-preferences">
          <h4>Notification Preferences</h4>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={settings.is_enabled}
              onChange={(e) => handleUpdateSettings({
                ...settings,
                is_enabled: e.target.checked
              })}
            />
            <span>Enabled</span>
          </label>

          <div className="reminder-time">
            <label>Notification Time:</label>
            <input
              type="time"
              value={settings.notification_time}
              onChange={(e) => handleUpdateSettings({
                ...settings,
                notification_time: e.target.value
              })}
            />
          </div>

          <div className="reminder-time">
            <label>Advance Notice (hours):</label>
            <input
              type="number"
              value={settings.advance_notice_hours}
              onChange={(e) => handleUpdateSettings({
                ...settings,
                advance_notice_hours: parseInt(e.target.value) || 0
              })}
              min="0"
              max="24"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationSettings;